import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const content = useSiteContent();
  const nav = content?.nav || {};

  const links = [
    { href: "#about", label: nav.about || "About" },
    { href: "#isigroup", label: nav.isigroup || "iSi Group" },
    { href: "#services", label: nav.services || "Services" },
    { href: "#testimonials", label: nav.testimonials || "Testimonials" },
    { href: "#contact", label: nav.contact || "Contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-8 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/75 backdrop-blur-xl border-b border-slate-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
        <a href="#top" data-testid="navbar-logo" className="flex items-center gap-2">
          <span className="font-serif-sc text-xl lg:text-2xl font-bold text-slate-900 tracking-tight">
            Charles<span className="text-orange-600">.</span>
          </span>
          <span className="hidden sm:inline-block h-4 w-px bg-slate-300" />
          <span className="hidden sm:inline-block text-xs tracking-[0.25em] uppercase text-slate-500 font-display">
            iSi Group
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className="text-sm text-slate-700 hover:text-orange-600 transition-colors duration-300 font-medium"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Button
            asChild
            data-testid="navbar-cta-button"
            className="hidden md:inline-flex bg-orange-600 hover:bg-orange-700 text-white rounded-full px-5 h-10 text-sm font-medium transition-all active:scale-95"
          >
            <a href="#contact">{nav.cta || "Get in Touch"}</a>
          </Button>
          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-slate-900"
            aria-label="menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-slate-200">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-slate-700 hover:text-orange-600"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="inline-flex justify-center bg-orange-600 text-white rounded-full px-5 py-3 text-sm font-medium"
            >
              {nav.cta || "Get in Touch"}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
